import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FreshdeskArticle } from '../interfaces/freshdesk-article.interface';

export interface LinkInformationFreshdeskArticle {
  articleId: number;
  title?: string;
  bodyHtml?: string;
  articleCreatedAt?: string;
  articleEditedAt?: string;

  // LinkInformation-specific
  index: number;
  linkInformationId: number;
}

export interface LinkArticleCreateRequest {
  articleId: number;
  index?: number;
}

export interface LinkArticleUpdateRequest {
  index: number;
}


export interface SupportPayload {
  articleId: number;
  rating: number | null;          // or number if always set
  reason: string | null;          // e.g. selected option
  details?: string | null;
  email?: string | null;
  diagnostics?: any;              // keep flexible
}

@Injectable({
  providedIn: 'root'
})
export class FreshdeskSolutionService {
  readonly url = 'freshdeskSolution';

  constructor(private http: HttpClient) { }

  /** Get articles for a linkInformation (resolved via linkId) */
  getLinkTemplateArticles(linkId: number): Observable<FreshdeskArticle[]> {
    return this.http.get<FreshdeskArticle[]>(`${this.url}/${linkId}`);
  }

  /** Get articles for a linkInformation (resolved via linkId) */
  getLinkInformationArticles(linkId: number): Observable<LinkInformationFreshdeskArticle[]> {
    return this.http.get<LinkInformationFreshdeskArticle[]>(`${this.url}/${linkId}/link-information-articles`);
  }

  getArticlesByIds(articleIds: number[]) {
    return this.http.post<any[]>(`${this.url}/articles/by-ids`, { articleIds });
  }

  /** Create a new article and link it */
  createArticleLink(linkId: number, articleId: number, index: number = 0): Observable<LinkInformationFreshdeskArticle> {
    const body: LinkArticleCreateRequest = { articleId, index };
    return this.http.post<LinkInformationFreshdeskArticle>(`${this.url}/${linkId}`, body);
  }

  /** Update the index of an article-link association */
  updateArticleLink(linkId: number, articleId: number, index: number): Observable<LinkInformationFreshdeskArticle> {
    const body: LinkArticleUpdateRequest = { index };
    return this.http.put<LinkInformationFreshdeskArticle>(`${this.url}/${linkId}/${articleId}`, body);
  }

  /** Delete an article-link association */
  deleteArticleLink(linkId: number, articleId: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/${linkId}/${articleId}`);
  }

  /** Batch update after drag-and-drop */
  batchUpdateIndices(linkId: number, items: { articleId: number; index: number }[]): Observable<void> {
    return this.http.put<void>(`${this.url}/${linkId}/batch`, items);
  }

  refreshArticles(linkId: number) {
    // Replace with actual API call as needed
    return this.http.post(`${this.url}/${linkId}/refresh-articles`, {});
  }

  submitSupport(payload: SupportPayload): Observable<boolean> {
    return this.http.post<boolean>(`${this.url}/support`, payload);
  }

  refreshPatternArticle(patternId: number, articleId: number) {
    return this.http.post<{
      patternId: number;
      refreshedArticleIds: number[];
      refreshedUtc: string;
    }>(`${this.url}/${patternId}/refresh-articles/${articleId}`, {});
  }

  refreshApplicationLoginArticle(applicationId: number, articleId: number) {
    return this.http.post<{
      applicationId: number;
      refreshedArticleIds: number[];
      refreshedUtc: string;
    }>(`${this.url}/application-login-articles/refresh`, { applicationId, articleId });
  }

  getMyRatingsByIds(articleIds: number[]) {
    return this.http.post<Record<string, number>>(
      `${this.url}/articles/ratings/by-ids`,
      { articleIds }
    );
  }

  upsertMyRating(articleId: number, rating: number) {
    return this.http.put<number>(
      `${this.url}/articles/${articleId}/rating`,
      { rating }
    );
  }

  deleteMyRating(articleId: number) {
    return this.http.delete<void>(
      `${this.url}/articles/${articleId}/rating`
    );
  }
}
